
import { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

gsap.registerPlugin(ScrollTrigger);


const OurStory = () => {
    const { t } = useTranslation();

    useEffect(() => {
        // Generic fade-in-up for all elements with .fade-in-up
        gsap.utils.toArray(".fade-in-up").forEach((el) => {
            gsap.fromTo(
                el,
                { y: 50, opacity: 0 },   // Start: slightly lower + invisible
                {
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: el,
                        start: "top 85%",   // When element enters viewport
                        toggleActions: "play none none reverse", // Animate in/out
                    },
                }
            );
        });
    }, []);

    const designCards = [
        { bg: "#9B29FF", img: "icon1.png", header: t('ourStory.dcard1Header'), text: t('ourStory.dcard1Text') },
        { bg: "#671BFF", img: "icon2.png", header: t('ourStory.dcard2Header'), text: t('ourStory.dcard2Text') },
        { bg: "#9B29FF", img: "icon1.png", header: t('ourStory.dcard3Header'), text: t('ourStory.dcard3Text') },
        { bg: "#671BFF", img: "icon2.png", header: t('ourStory.dcard4Header'), text: t('ourStory.dcard4Text') },
    ];

  return (
    <div>
        <div className="absolute inset-0 z-0 w-screen left-1/2 right-1/2 -mx-[50vw]">
            <img className="w-full" src={require("../media/images/StarSplash.png")} />
        </div>

        <div className='flex justify-center flex-col items-center mb-24 lg:min-h-[65vh]'>
            <h1 className='text-4xl md:text-6xl text-[#101223] max-w-[650px] text-center leading-[1.125] fade-in-up'>
                {t('ourStory.headline1')}<br/> {t('ourStory.headline2')} <span className="text-[#9B29FF]">{t('ourStory.headline3')}</span> {t('ourStory.headline4')}
            </h1>
        </div>

        <div className="relative w-screen h-96 md:h-[500px] lg:h-[500px] xl:h-[600px] 2xl:h-[700px] mb-12 overflow-hidden left-1/2 -translate-x-1/2">
            <img
                className="absolute inset-0 w-full h-full object-cover transform object-top"
                src={require("../media/images/analytics-mockup.png")}
                alt="Analytics dashboard screenshot"
            />
        </div>

        <div className="bg-white rounded-3xl py-12 md:py-32 mb-12 -mt-32 md:-mt-36 relative z-10">
            <div className="ml-responsive mr-responsive flex relative px-12 md:px-0">
                <div className="w-full md:w-[55%]">
                    <div className="sm:pr-12">
                        <p className="fade-in-up text-sm sm:text-base">
                            {t('ourStory.body1')}
                        </p>

                        <p className="fade-in-up text-sm sm:text-base mt-6">
                            {t('ourStory.body2')}
                        </p>

                        <h2 className="fade-in-up text-lg sm:text-2xl mt-6 text-black">
                            {t('ourStory.body3Prefix')} <span className="text-[#9B29FF]">{t('ourStory.body3Highlight')}</span>{t('ourStory.body3Suffix')}
                        </h2>

                    </div>
                </div>
                <div className="w-[45%] relative hidden md:block z-30">
                    <img
                        className="absolute w-full xl:-top-20 right-0 fade-in-up"
                        src={require("../media/images/person2.png")}></img>
                </div>
            </div>
        </div>

        <div className='mt-48 flex flex-col items-center justify-center mb-32 leading-[1.125] fade-in-up'>
            <h2 className='text-4xl md:text-6xl text-black text-center max-w-[800px]'>
                {t('ourStory.headline2Text')} <br/>{t('ourStory.headline2Rest')} <span className="text-[#9B29FF]">{t('ourStory.headline2Highlight')}</span> {t('ourStory.headline2End')}
            </h2>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-12 mb-32'>
            <p className='text-sm fade-in-up'>{t('ourStory.col1')}</p>
            <p className='text-sm fade-in-up'>{t('ourStory.col2')}</p>
            <p className='text-sm fade-in-up'>{t('ourStory.col3')}</p>
        </div>

        <div className="flex-col md:flex-row justify-center mb-32 relative hidden md:flex">
            <div className="flex gap-8 bg-background-gradient justify-around items-center px-12 py-14 rounded-3xl mx-auto w-full fade-in-up">
                <h1 className="text-4xl">{t('ourStory.ctaHeading')}</h1>
                <h2 className="text-xl text-center max-w-[600px]">{t('ourStory.ctaSubtitle')}</h2>
                <Link to="/demo" className="bg-white text-[#9B29FF] py-2 px-6 rounded-full hover:bg-gray-200 transition-colors">
                    {t('ourStory.ctaButton')}
                </Link>
            </div>
        </div>

        <div className='relative mb-48 w-full'>
            <img
                src={require("../media/images/bg-circle-wide.png")}
                alt="Background"
                className="absolute top-14 left-1/2 -translate-x-1/2
                        w-[2000px] max-w-none
                        object-contain z-0 pointer-events-none select-none"
            />

            <div className='relative z-10'>

                <h1 className="text-center text-4xl md:text-6xl text-black fade-in-up">{t('ourStory.designedHeading')}</h1>
                <p className="text-center text-base mt-12 mb-20 fade-in-up">{t('ourStory.designedBody')}</p>

                <div className="flex flex-col justify-center relative">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 z-10 mx-auto w-full">
                        {designCards.map((card, index) => (
                            <div
                                key={index}
                                className="p-6 sm:p-8 rounded-xl flex-1 shadow leading-7 relative overflow-hidden
                                        transition-all duration-300 ease-in-out
                                        hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]  fade-in-up"
                                style={{ backgroundColor: card.bg }}
                            >
                                {/* Gradient Overlay Effect */}
                                <div className="absolute inset-0 bg-white opacity-0 transition-opacity duration-300 hover:opacity-10"></div>

                                <img className="mb-4 relative z-10 w-10 sm:w-auto" src={require(`../media/images/${card.img}`)} alt={card.header} />
                                <h3 className="text-3xl mb-4">{card.header}</h3>
                                <p className="text-white text-base relative z-10">{card.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>


        </div>

    </div>
  );
};

export default OurStory;
